import {useStore} from '@tanstack/react-form';
import {useEffect, useMemo, useRef, useState} from 'react';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import {getTypedObjectKeys} from '../../../common/shared/utils/getTypedObjectKeys';
import {twMerge} from '../../../common/twMerge';
import {parseSchemaToFields} from '../../../common/zodForm/helper';
import {FormField} from '../../../common/zodForm/type';
import {Text} from '../../../design-system/atoms/Text/Text';
import {FormGroupV2} from '../../../design-system/molecules/FormGroupV2/FormGroupV2';
import {SelectV2} from '../../../design-system/molecules/SelectV2/SelectV2';
import {FileListV2} from '../../../design-system/organisms/FileList/FileListV2';
import {showToast} from '../../../toast/toastHandler';
import {
  ASK_CATEGORY_GLOBAL_OPTIONS,
  ASK_CATEGORY_KO_OPTIONS,
  ASK_SUB_CATEGORY_OPTIONS,
  ASK_TYPE_TRANS_KEY_MAP,
  AskSubCategory,
  AskType,
  BANK_ACCOUNT_EXCLUDED_REGEX,
  CATEGORY_ID_MAP,
  CATEGORY_ID_MAP_GLOBAL,
  MAX_FILES,
  PHONE_EXCLUDED_REGEX,
} from '../constant';
import {
  DEFAULT_HIDDEN_FIELDS,
  FormFieldKeys,
  getFormFieldSchema,
  SHOW_WHEN_DEFAULT_NOT_EXISTS_FIELDS_MAP,
} from '../schema/AskRequestSchema';
import {useAskRequestForm} from '../util/AskRequestForm';
import {getRequiredFieldFilled} from '../util/getRequiredFieldFilled';
import {isFilesContainNotAllowedExtentions} from '../util/isFilesContainNotAllowedExtentions';
import {isFilesOverLimitSize} from '../util/isFilesOverLimitSize';

export interface AskRequestProps {
  isWebView?: boolean;
  onSubmit: (data: FormData) => Promise<void>;
  initialAskType?: AskType;
  initialAskSubCategory?: AskSubCategory;
  initialDefaultValues?: Record<string, unknown>;
  isLogin?: boolean;
  isLoading?: boolean;
}

export function AskRequest({
  isWebView,
  onSubmit,
  initialAskType = AskType.Default,
  initialAskSubCategory = AskSubCategory.Default,
  initialDefaultValues,
  isLogin,
  isLoading,
}: AskRequestProps) {
  const fieldRefs = useRef<{
    [key: string]: HTMLElement | null;
  }>({});
  const [askType, setAskType] = useState<AskType>(initialAskType);
  const {isMobile} = requestContext.requestInfo.display;
  const isGlobal = requestContext.requestInfo.isGlobal;
  const [askSubCategory, setAskSubCategory] = useState<AskSubCategory>(
    initialAskSubCategory
  );

  const askSchemaMap = getFormFieldSchema();
  const currentSchema =
    askType !== AskType.Default && !!askSubCategory
      ? askSchemaMap[askType][askSubCategory]
      : askSchemaMap[AskType.Default][AskSubCategory.Default];

  const {formFields, defaultValues} = useMemo((): {
    formFields: FormField[];
    defaultValues: Record<string, unknown>;
  } => {
    const {fields: formFields} = parseSchemaToFields(currentSchema);

    const defaultValues = formFields.reduce((acc, field) => {
      return {
        ...acc,
        [field.key]: initialDefaultValues?.[field.key] ?? field.default,
      };
    }, {});

    return {
      formFields,
      defaultValues,
    };
  }, [currentSchema, initialDefaultValues]);

  const Form = useAskRequestForm({
    defaultValues,
    onSubmit: async ({value}) => {
      const formData = new FormData();

      Object.entries(value).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        if (key === 'files') {
          for (const file of value as File[]) {
            formData.append(key, file);
          }
        } else if (typeof value === 'string') {
          formData.append(key, value.trim());
        } else {
          // @ts-expect-error
          formData.append(key, value);
        }
      });

      try {
        await onSubmit(formData);
        Form.reset();
        setAskType(AskType.Default);
        setAskSubCategory(initialAskSubCategory);
      } catch {
        // container 에서 토스트 띄움. 별도 처리 필요 없음
      }
    },
    /**
     * tanstack/form의 validators.onSubmit은 기본적으로 값이 변경될 때만 체크합니다.
     * submit 이후에 값이 변경되지 않는다면 validator는 동작하지 않아 invalid하더라도 submit이 동작할 수 있도록 변경하여 validator가 동작할 수 있도록 합니다.
     */
    canSubmitWhenInvalid: true,
    validators: {
      onSubmit: ({value}) => {
        const result = currentSchema.safeParse(value);
        if (!result.success) {
          const errorMap = result.error?.issues.reduce(
            (acc, issue) => {
              return {
                ...acc,
                [issue.path[0]]: issue.message,
              };
            },
            {} as Record<string, string | undefined>
          );

          const firstErrorField = getTypedObjectKeys(fieldMeta).find(
            key => !!errorMap[key]
          );
          if (firstErrorField) {
            const ref = fieldRefs.current[firstErrorField];
            if (ref) {
              ref.focus();
              ref.scrollIntoView({
                block: 'center',
              });
            }
          }
          return {
            fields: errorMap,
          };
        }
      },
    },
  });

  const values = useStore(Form.store, state => state.values);
  const fieldMeta = useStore(Form.store, state => state.fieldMeta);
  const disabledSubmit = useMemo(() => {
    return (
      isLoading ||
      !getRequiredFieldFilled({
        formFields,
        value: values,
      })
    );
  }, [formFields, values, isLoading]);

  const isSelectedCategory = !!askType && askType !== AskType.Default;
  const isSelectedSubCategory =
    isSelectedCategory &&
    !!askSubCategory &&
    askSubCategory !== AskSubCategory.Default;

  useEffect(() => {
    if (
      askType &&
      askSubCategory &&
      askType !== AskType.Default &&
      askSubCategory !== AskSubCategory.Default
    ) {
      const categoryId = isGlobal
        ? CATEGORY_ID_MAP_GLOBAL[askType][askSubCategory]
        : CATEGORY_ID_MAP[askType][askSubCategory];
      if (!categoryId) {
        throw new Error(
          `카테고리 ID(${askType} > ${askSubCategory})가 존재하지 않습니다.`
        );
      }
      Form.setFieldValue('categoryId', categoryId);
      Form.setFieldValue(
        'subject',
        `${t(ASK_TYPE_TRANS_KEY_MAP[askType])} > ${askSubCategory.toLocaleLowerCase()}`
      );
    }
  }, [askType, askSubCategory, Form.setFieldValue, isGlobal]);

  const scrollTextareaToTop = () => {
    Object.values(fieldRefs.current).forEach(element => {
      if (element instanceof HTMLTextAreaElement) {
        element.scrollTop = 0;
      }
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col px-16pxr py-15pxr self-stretch bg-theme-solid-10">
        <Text className="font-small2-bold text-theme-solid-100">
          {t('more_customer_cs_inquiry_tab_info_1')}
        </Text>
        <Text className="font-small2 text-theme-solid-100">
          {t('more_customer_cs_inquiry_tab_info_2')}
        </Text>
      </div>

      <div className="flex flex-col pt-20pxr">
        <form
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            Form.handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <div className="px-15pxr">
            <FormGroupV2
              isMobile={isMobile}
              status={'normal'}
              label={t('more_customer_cs_inquiry_tab_category')}
              required={true}
            >
              {id => (
                <div
                  className={twMerge(
                    'flex w-full',
                    isMobile ? 'flex-col gap-10pxr' : 'flex-row gap-8pxr'
                  )}
                >
                  <SelectV2
                    id={id}
                    value={askType}
                    onChange={value => {
                      setAskType(value as AskType);
                      if (!isWebView) {
                        setAskSubCategory(AskSubCategory.Default);
                      }
                      Form.reset();
                      scrollTextareaToTop();
                    }}
                    isMobile={isMobile}
                    options={
                      isGlobal
                        ? ASK_CATEGORY_GLOBAL_OPTIONS
                        : ASK_CATEGORY_KO_OPTIONS
                    }
                    placeholder={t(
                      'more_customer_cs_inquiry_tab_category_select'
                    )}
                  />
                  {!isWebView && (
                    <SelectV2
                      value={askSubCategory}
                      onChange={value => {
                        setAskSubCategory(value as AskSubCategory);
                        Form.reset();
                        scrollTextareaToTop();
                      }}
                      isMobile={isMobile}
                      options={ASK_SUB_CATEGORY_OPTIONS}
                      placeholder={t(
                        'more_customer_cs_inquiry_tab_category_select'
                      )}
                      disabled={!isSelectedCategory}
                      data-test="ask-sub-category"
                    />
                  )}
                </div>
              )}
            </FormGroupV2>

            {formFields.map(formField => {
              const {metadata, uiType, key} = formField;
              // 폼 UI상 미노출되거나 특정 카테고리에서만 노출되는 필드를 필터링합니다.
              const showField =
                !DEFAULT_HIDDEN_FIELDS.includes(key) ||
                (SHOW_WHEN_DEFAULT_NOT_EXISTS_FIELDS_MAP[askType]?.[
                  askSubCategory
                ].includes(key as FormFieldKeys) &&
                  !defaultValues[key]);

              const hideFileFieldForNonLogin = key === 'files' && !isLogin;

              if (!showField || hideFileFieldForNonLogin) {
                return null;
              }

              if (uiType === 'text') {
                const textFieldMetadata =
                  metadata?.uiType === 'text' ? metadata : undefined;

                return (
                  <Form.AppField
                    name={key}
                    key={key}
                    validators={{
                      onChange: ({value}) =>
                        textFieldMetadata?.validators?.onChange?.(
                          value as string
                        ),
                    }}
                  >
                    {field => {
                      const isStatusError = field.state.meta.errors.length > 0;

                      return (
                        <field.TextField
                          isMobile={isMobile}
                          label={metadata?.label || key}
                          subLabel={
                            isStatusError
                              ? field.state.meta.errorMap['onSubmit']
                              : metadata?.subLabel
                          }
                          inputProps={{
                            ...textFieldMetadata?.inputProps,
                            ref: ref => {
                              fieldRefs.current[key] = ref;
                            },
                            disabled: !isSelectedSubCategory,
                            value: field.state.value as string,
                            onChange: e => field.handleChange(e.target.value),
                            status: isStatusError ? 'error' : 'normal',
                            onInput: e => {
                              if (key === 'telNum') {
                                e.currentTarget.value =
                                  e.currentTarget.value.replace(
                                    PHONE_EXCLUDED_REGEX,
                                    ''
                                  );
                              } else if (key === 'bankAccount') {
                                e.currentTarget.value =
                                  e.currentTarget.value.replace(
                                    BANK_ACCOUNT_EXCLUDED_REGEX,
                                    ''
                                  );
                              }
                            },
                          }}
                          required={formField.required}
                          className="my-16pxr"
                        />
                      );
                    }}
                  </Form.AppField>
                );
              } else if (uiType === 'textarea') {
                const textareaFieldMetadata =
                  metadata?.uiType === 'textarea' ? metadata : undefined;

                return (
                  <Form.AppField
                    name={key}
                    key={key}
                    validators={{
                      onChange: ({value}) =>
                        textareaFieldMetadata?.validators?.onChange?.(
                          value as string
                        ),
                    }}
                  >
                    {field => {
                      const textareaValue = field.state.value as string;
                      const isStatusError = field.state.meta.errors.length > 0;

                      return (
                        <field.TextAreaField
                          isMobile={isMobile}
                          label={metadata?.label || key}
                          subLabel={
                            isStatusError
                              ? field.state.meta.errorMap['onSubmit']
                              : ''
                          }
                          textareaProps={{
                            ...textareaFieldMetadata?.textareaProps,
                            ref: ref => {
                              fieldRefs.current[key] = ref;
                            },
                            disabled: !isSelectedSubCategory,
                            value: field.state.value as string,
                            onChange: e => field.handleChange(e.target.value),
                            status: isStatusError ? 'error' : 'normal',
                            className: 'h-100pxr',
                          }}
                          required={formField.required}
                          currentValue={textareaValue?.length || 0}
                          maxValue={metadata?.maxValue}
                          className="my-16pxr"
                        />
                      );
                    }}
                  </Form.AppField>
                );
              } else if (uiType === 'fileUpload') {
                const fileUploadFieldMetadata =
                  metadata?.uiType === 'fileUpload' ? metadata : undefined;

                return (
                  <Form.AppField
                    name={key}
                    key={key}
                    validators={{
                      onChange: ({value}) =>
                        value &&
                        fileUploadFieldMetadata?.validators?.onChange?.(
                          value as File[]
                        ),
                    }}
                  >
                    {field => {
                      const fileList = Array.from(
                        (field.state.value as FileList | undefined) || []
                      );

                      const totalFileSize = (
                        fileList.reduce((acc, file) => acc + file.size, 0) /
                        (1024 * 1024)
                      ).toFixed(2);

                      return (
                        <field.FileUploadField
                          isMobile={isMobile}
                          label={metadata?.label || key}
                          fileUploadProps={{
                            ...fileUploadFieldMetadata?.fileUploadProps,
                            disabled: !isSelectedSubCategory,
                            onChange: event => {
                              // 파일 업로드 시, 벨리데이션을 통과하지 못할 경우 업로드 자체를 막아야합니다.
                              const newFileList = [
                                ...(fileList ?? []),
                                ...(event.target.files ?? []),
                              ];

                              // input.value가 존재할 경우 동일한 파일을 선택했을 때 onChange가 발생되지 않기 때문에 해당값을 초기화해줍니다.
                              event.target.value = '';

                              // 파일 밸리데이션은 파일 개수, 파일 용량 순으로 체크합니다.
                              if (newFileList.length > MAX_FILES) {
                                showToast({
                                  message: t(
                                    'more_customer_cs_inquiry_tab_attachment_count_toast'
                                  ),
                                  isMobile,
                                });
                                return;
                              }

                              if (isFilesOverLimitSize(newFileList)) {
                                showToast({
                                  message: t(
                                    'more_customer_cs_inquiry_tab_attachment_capacity_toast'
                                  ),
                                  isMobile,
                                });
                                return;
                              }

                              if (
                                isFilesContainNotAllowedExtentions(newFileList)
                              ) {
                                showToast({
                                  message: t(
                                    'more_customer_cs_inquiry_tab_attachment_invalid_file_extension'
                                  ),
                                  isMobile,
                                });
                                return;
                              }

                              field.handleChange(newFileList);
                              showToast({
                                message: t(
                                  'more_customer_cs_inquiry_tab_attachment_success_toast'
                                ),
                                isMobile,
                              });
                            },
                            multiple: true,
                          }}
                          subLabel={metadata?.subLabel}
                          currentValue={fileList.length > 0 ? totalFileSize : 0}
                          maxValue={metadata?.maxValue}
                          additionalInfo={
                            fileList.length > 0 ? (
                              <FileListV2
                                fileList={fileList}
                                onClickRemoveFile={file => {
                                  if (!fileList) return;
                                  const updatedFileList = fileList.filter(
                                    existingFile => existingFile !== file
                                  );
                                  showToast({
                                    message: t(
                                      'more_customer_cs_inquiry_tab_attachment_delete_toast'
                                    ),
                                    isMobile,
                                  });
                                  field.handleChange(updatedFileList);
                                }}
                              />
                            ) : undefined
                          }
                          className="my-16pxr"
                        />
                      );
                    }}
                  </Form.AppField>
                );
              }
            })}
          </div>

          <Form.SubmitButton
            label={t('more_customer_cs_inquiry_tab_save_button')}
            className={twMerge(
              'pt-4pxr',
              disabledSubmit && 'active:text-theme-solid-20'
            )}
            buttonProps={{disabled: disabledSubmit}}
          />
        </form>
      </div>
    </div>
  );
}
